"use client";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Logo from "@/components/ui/icons/logo";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

interface FooterProps {
  className?: string;
}

export function Footer({ className }: FooterProps) {
  const router = useRouter();
  const t = useTranslations('footer');
  const tSections = useTranslations('footer.sections');
  const tServices = useTranslations('footer.additionalServices');
  const redirectTo = (href: string) => {
    router.push(href);
  };
  return (
    <footer role="contentinfo" className={cn("bg-black pt-16 pb-8 rounded-t-[60px]", className)}>
      <div className="container mx-auto px-4  md:px-20 landscape-phone:px-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-16">
          <div className="col-span-2 font-outfit md:col-span-1 text-center md:text-left">
            <div className="mb-4 flex justify-center md:justify-start">
              <Logo aria-label="Miller40 Logo" width={180} height={45} />
            </div>
            <p className="text-white mb-8">{t('tagline')}</p>

            <div className="mb-8">
              <p className="text-white mb-4">
                {t('hiring')}
              </p>
              <Button
                onClick={() => redirectTo("/about/contact")}
                className="bg-transparent hover:bg-yellow-600 text-yellow-500 hover:text-black border border-yellow-500 hover:border-transparent rounded-full px-6"
              >
                {t('applyNow')}
              </Button>
            </div>
          </div>

          <div className="col-span-1 md:col-span-1">
            <h3 className="font-medium text-yellow-500 mb-6">{tSections('ourServices.title')}</h3>
            <ul className="space-y-4 text-white">
              <li>
                <Link href="/services/auto-services?category=mechanic">
                  {tSections('ourServices.mechanicServices')}
                </Link>
              </li>
              <li>
                <Link href="/services/auto-services?category=tires">
                  {tSections('ourServices.tireServices')}
                </Link>
              </li>
              <li>
                <Link href="/services/collision-repair">{tSections('ourServices.collisionRepair')}</Link>
              </li>
            </ul>
          </div>

          <div className="col-span-1 md:col-span-1">
            <h3 className="font-medium text-yellow-500 mb-6">{tSections('company.title')}</h3>
            <ul className="space-y-4 text-white">
              <li>
                <Link href="/about/us">{tSections('company.whoWeAre')}</Link>
              </li>
              <li>
                <Link href="/about/blog">{tSections('company.blog')}</Link>
              </li>
              <li>
                <Link href="/about/contact">{tSections('company.contactUs')}</Link>
              </li>
            </ul>
          </div>

          <div className="col-span-1 md:col-span-1">
            <h3 className="font-medium text-yellow-500 mb-6">{tSections('locations.title')}</h3>
            <ul className="space-y-4 text-white">
              <li>
                <Link href="/locations?facility=service-center">
                  {tSections('locations.serviceCenters')}
                </Link>
              </li>
              <li>
                <Link href="/locations?facility=collision-center">
                  {tSections('locations.collisionCenters')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal — separate column so it doesn't break the mobile grid */}
          <div className="col-span-1 md:col-span-1">
            <h3 className="font-medium text-yellow-500 mb-6">{tSections('legal.title')}</h3>
            <ul className="space-y-4 text-white">
              <li>
                <Link href="/return-policy">
                  {tSections('legal.returnPolicy')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Minimal additional services for SEO */}
        <div className="border-t border-zinc-800/50 pt-6 pb-4">
          <div className="text-center">
            <p
              id="footer-additional-services"
              className="text-xs text-gray-400 mb-3 uppercase tracking-wider"
            >
              {tServices('title')}
            </p>
            {/* Named from the heading already above it rather than a new
                string, so the landmark stays translated for free. */}
            <nav
              aria-labelledby="footer-additional-services"
              className="flex flex-wrap justify-center gap-3 text-xs"
            >
              <Link
                href="/services/auto-services?category=mechanic&service=oil-change"
                className="text-gray-400 hover:text-yellow-500 transition-colors"
              >
                {tServices('oilChanges')}
              </Link>
              <span className="text-gray-600">•</span>
              <Link
                href="/services/auto-services?category=tires&service=tire-installation"
                className="text-gray-400 hover:text-yellow-500 transition-colors"
              >
                {tServices('tireInstallation')}
              </Link>
              <span className="text-gray-600">•</span>
              <Link
                href="/services/auto-services?category=tires&service=brake-service"
                className="text-gray-400 hover:text-yellow-500 transition-colors"
              >
                {tServices('brakeRepair')}
              </Link>
              <span className="text-gray-600">•</span>
              <Link
                href="/services/auto-services?category=mechanic&service=ac-service"
                className="text-gray-400 hover:text-yellow-500 transition-colors"
              >
                {tServices('acService')}
              </Link>
              <span className="text-gray-600">•</span>
              <Link
                href="/services/auto-services?category=mechanic&service=diagnostics"
                className="text-gray-400 hover:text-yellow-500 transition-colors"
              >
                {tServices('diagnostics')}
              </Link>
            </nav>
          </div>
        </div>

        <div className="border-t border-zinc-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              <p className="mb-1">{t('copyright')}</p>
              <p className="text-xs text-gray-500">
                {t('compliance')}
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-medium text-yellow-500 mb-4 text-sm">
                {t('followUs')}
              </h3>
              <div className="flex justify-center space-x-6">
                <SocialIcon href="https://www.facebook.com/Miller40AutoGroup/" type="facebook" />
                <SocialIcon href="https://www.instagram.com/miller40carcare" type="instagram" />
                <SocialIcon href="https://www.yelp.com/biz/miller-40-car-care-and-tire-center-miami" type="yelp" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

interface SocialIconProps {
  href: string;
  type: "facebook" | "instagram" | "yelp";
}

function SocialIcon({ href, type }: SocialIconProps) {
  const icons = {
    facebook: (
      <svg
        className="h-5 w-5"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
          clipRule="evenodd"
        />
      </svg>
    ),
    instagram: (
      <svg
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7.75 2h8.5A5.75 5.75 0 0122 7.75v8.5A5.75 5.75 0 0116.25 22h-8.5A5.75 5.75 0 012 16.25v-8.5A5.75 5.75 0 017.75 2zM16 11.37a4 4 0 11-7.999-.001A4 4 0 0116 11.37zm2.5-4.87a1 1 0 11-2.001.001A1 1 0 0118.5 6.5z"
        />
      </svg>
    ),
    yelp: (
      <svg
        className="h-5 w-5"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M20.16 12.73l-4.42 1.27a1.15 1.15 0 01-1.41-1.04l-.02-.17c-.07-.64.37-1.2 1-1.31l4.54-.78c.63-.11 1.22.33 1.3.97l.01.1c.08.63-.36 1.21-.99 1.32l-.01-.36zM12.93 9.4l2.07-4.1c.29-.57.04-1.27-.55-1.55l-.09-.04a1.15 1.15 0 00-1.55.56L10.73 8.4c-.28.57-.03 1.26.56 1.54l.09.04c.57.28 1.27.03 1.55-.58zm-1.5 8.22l.64 4.52c.09.64.69 1.08 1.33.98l.1-.02c.63-.1 1.07-.7.97-1.33l-.65-4.52c-.09-.64-.69-1.07-1.33-.97l-.09.01c-.64.1-1.07.7-.97 1.33zm-3.73-2.23l-3.8 2.6c-.52.36-.65 1.08-.29 1.6l.06.08c.36.52 1.07.65 1.59.29l3.8-2.6c.52-.36.65-1.07.29-1.6l-.06-.08c-.36-.52-1.07-.65-1.59-.29zm-.5-5.5L3.1 9.2c-.62-.16-1.25.22-1.41.84l-.03.1c-.16.62.22 1.25.84 1.41l4.11.69c.62.16 1.25-.22 1.41-.84l.03-.1c.16-.63-.22-1.25-.85-1.41z"/>
      </svg>
    )
  };

  return (
    <Link 
      href={href} 
      className="text-yellow-500 hover:text-yellow-400"
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="sr-only">
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
      {icons[type]}
    </Link>
  );
}
